// @ts-strict-ignore
import Form from 'antd/lib/form';
import Space from 'antd/lib/space';
import Radio from 'antd/lib/radio';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import Typography from 'antd/lib/typography';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';
import { isEqual } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { tryUpdateAppSettings } from '../../../../actions/appSettings';
import { AppSettingsUpdatable } from '../../../../actions/updatable';
import { iconAndAltForIntegrationType } from '../../../../helpers/integrationHelpers';
import { ReleaseBranchPattern, Integration } from '../../../../types';
import { SetupViewProps } from '../../../../typescriptHelpers';
import { antGrey, runwayColors } from '../../../styles/globalStyles';
import { globalMessage } from '../../../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import SupportedTokensComponent from '@/components/common/SupportedTokensComponent';
import { AllPatternTokens, DisallowedTokensForBranch } from '@/types';
import TooltipInfoIcon from '../../../helperComponents/TooltipInfoIcon';

export interface ReleaseBranchModuleProps extends SetupViewProps {
    releaseBranchPattern?: ReleaseBranchPattern;
    workingBranch?: string;
    vcsIntegration?: Integration;
    releaseBranchPatternGuess?: string;
    isLoadingReleaseBranchPatternGuess?: boolean;
}
export const ReleaseBranchModule: React.FC<ReleaseBranchModuleProps> = ({
    appId,
    orgId,
    onChange,
    releaseBranchPattern,
    workingBranch,
    vcsIntegration,
    releaseBranchPatternGuess,
    isLoadingReleaseBranchPatternGuess,
}) => {
    const [form] = useForm();

    const [integrationIcon, integrationIconAlt] = vcsIntegration ? iconAndAltForIntegrationType(vcsIntegration.id) : [];

    const dispatch = useDispatch();

    const addReleaseBranchPatternIsLoading = useIsLoading(AppSettingsUpdatable.NewReleaseBranchPattern);
    const addReleaseBranchPatternError = useError(AppSettingsUpdatable.NewReleaseBranchPattern);
    const addReleaseBranchPatternSuccess = useUpdated(AppSettingsUpdatable.NewReleaseBranchPattern);

    React.useEffect(() => {
        if (addReleaseBranchPatternSuccess) {
            globalMessage.success('Release branch added!');
            onChange(false);
        }
    }, [addReleaseBranchPatternSuccess]);

    const addReleaseBranchPattern = (values) => {
        const pattern = values.shouldReuseWorkingBranch ? workingBranch : values.releaseBranchPattern;
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.NewReleaseBranchPattern, {
                orgId,
                appId,
                pattern,
                isHotfix: false,
            }),
        );
    };

    const editReleaseBranchPatternIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleaseBranchPattern);
    const editReleaseBranchPatternError = useError(AppSettingsUpdatable.UpdateReleaseBranchPattern);
    const editReleaseBranchPatternSuccess = useUpdated(AppSettingsUpdatable.UpdateReleaseBranchPattern);

    React.useEffect(() => {
        if (editReleaseBranchPatternSuccess) {
            globalMessage.success('Release branch updated!');
            onChange(false);
        }
    }, [editReleaseBranchPatternSuccess]);

    React.useEffect(() => {
        if (editReleaseBranchPatternError) {
            globalMessage.error(editReleaseBranchPatternError);
        }
    }, [editReleaseBranchPatternError, addReleaseBranchPatternError]);

    const editReleaseBranchPattern = (values) => {
        const pattern = values.shouldReuseWorkingBranch ? workingBranch : values.releaseBranchPattern;
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleaseBranchPattern, {
                orgId,
                appId,
                pattern,
                isHotfix: false,
                branchPatternId: releaseBranchPattern ? releaseBranchPattern.id : null,
            }),
        );
    };

    const isSameAsWorkingBranch = releaseBranchPattern?.pattern === workingBranch;

    const initialValues = {
        releaseBranchPattern: releaseBranchPattern && !isSameAsWorkingBranch ? releaseBranchPattern.pattern : null,
        shouldReuseWorkingBranch: isSameAsWorkingBranch,
    };

    const checkIfHasUnsavedChanges = () => {
        const formValues = form.getFieldsValue();
        return !isEqual(formValues, initialValues);
    };

    const onApplySuggestionButton = () => {
        form.setFieldsValue({ releaseBranchPattern: releaseBranchPatternGuess });
        onChange(checkIfHasUnsavedChanges());
    };
    const availableBranchTokens = AllPatternTokens.filter((token) => !DisallowedTokensForBranch.includes(token));

    return (
        <Form
            form={form}
            onFinish={releaseBranchPattern ? editReleaseBranchPattern : addReleaseBranchPattern}
            initialValues={initialValues}
            onFieldsChange={() => onChange(checkIfHasUnsavedChanges())}
        >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'end',
                        justifyContent: 'space-between',
                    }}
                >
                    <Form.Item style={{ flex: 1, marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Form.Item name="shouldReuseWorkingBranch" style={{ flex: 1, marginBottom: 0 }}>
                                <Radio.Group>
                                    <Space direction="vertical">
                                        <Radio value={false}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {integrationIcon && (
                                                    <img
                                                        src={integrationIcon}
                                                        alt={integrationIconAlt}
                                                        width={25}
                                                        height={25}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                )}
                                                <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                                                    <Form.Item noStyle name="releaseBranchPattern">
                                                        <Input
                                                            placeholder="Branch name or pattern"
                                                            autoComplete="off"
                                                            disabled={
                                                                form.getFieldValue('shouldReuseWorkingBranch') === true
                                                            }
                                                        />
                                                    </Form.Item>
                                                    <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                                                        <SupportedTokensComponent
                                                            availableTokens={availableBranchTokens}
                                                            prefixText="Pattern strings with"
                                                        />
                                                        <Text type="secondary">
                                                            Read more in our{' '}
                                                            <Typography.Link
                                                                href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                                                                target="_blank"
                                                            >
                                                                docs.
                                                            </Typography.Link>
                                                        </Text>
                                                    </Space>
                                                    {isLoadingReleaseBranchPatternGuess && <Spin size="small" />}
                                                    {releaseBranchPatternGuess && (
                                                        <Alert
                                                            style={{
                                                                marginTop: '1em',
                                                                marginBottom: '1em',
                                                                backgroundColor: `${runwayColors.primary}10`,
                                                                border: `1px solid ${runwayColors.primary}50`,
                                                            }}
                                                            message={
                                                                <>
                                                                    Suggested:{' '}
                                                                    <Text code style={{ margin: '1em' }}>
                                                                        {releaseBranchPatternGuess}
                                                                    </Text>
                                                                    <TooltipInfoIcon tooltipContent="Suggestion for this field based on the branches we found in your repository" />
                                                                </>
                                                            }
                                                            action={
                                                                <Button size="small" onClick={onApplySuggestionButton}>
                                                                    Apply
                                                                </Button>
                                                            }
                                                        />
                                                    )}
                                                </Form.Item>
                                            </div>
                                        </Radio>
                                        <Radio value={true} style={{ color: antGrey.grey8 }}>
                                            Our release branch is the same as our working branch.
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        )}
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Button
                                loading={editReleaseBranchPatternIsLoading || addReleaseBranchPatternIsLoading}
                                disabled={!checkIfHasUnsavedChanges()}
                                type="primary"
                                htmlType="submit"
                                icon={<ArrowRightOutlined />}
                            >
                                Save
                            </Button>
                        )}
                    </Form.Item>
                </div>
            </Space>
        </Form>
    );
};
